import { useQuery } from "@tanstack/react-query";
import { type Action, can } from "../access.js";
import { fetchMyAccess, fetchPeople } from "../api/index.js";
import { useWorkspaceView } from "../chosen-workspace.js";
import { queryKeys } from "../query-keys.js";

/** The people in a workspace with their access, the open invites, and the brands to give. */
export function usePeople(workspaceId: string) {
  return useQuery({
    queryKey: queryKeys.people(workspaceId),
    queryFn: () => fetchPeople(workspaceId),
  });
}

/** What the signed-in person may see and change in this workspace. */
export function useMyAccess(workspaceId: string) {
  return useQuery({
    queryKey: queryKeys.myAccess(workspaceId),
    queryFn: () => fetchMyAccess(workspaceId),
    enabled: workspaceId !== "",
  });
}

/**
 * Whether to offer an action. Nothing is offered until the access has loaded: a button that appears and then vanishes is
 * worse than one that appears a moment late. The server refuses either way.
 */
export function useCanIn(workspaceId: string, action: Action): boolean {
  const access = useMyAccess(workspaceId);
  return access.data === undefined ? false : can(access.data, action);
}

/** `useCanIn` for a screen inside a workspace. */
export function useCan(action: Action): boolean {
  return useCanIn(useWorkspaceView().workspaceId, action);
}

/**
 * Whether a whole screen may be offered: "unknown" until the access loads, so
 * a page does not flash a refusal on its way to opening — a new owner's empty
 * workspace on its way to setup, for one.
 */
export function useMay(
  workspaceId: string | null,
  action: Action,
): "yes" | "no" | "unknown" {
  const access = useMyAccess(workspaceId ?? "");
  if (workspaceId === null) return "yes";
  if (access.data !== undefined) return can(access.data, action) ? "yes" : "no";
  // A failed read is not a refusal: the screen opens, and the server decides.
  return access.isError ? "yes" : "unknown";
}
